import { Divider, Radio, Stack, Typography } from '@mui/material';
import type { InventoryResource, Snapshot } from '@repo/api-gw-sdk';
import classNames from 'classnames';
import { useState } from 'react';

import { MultiAccordionsItem } from '@/components/accordion/multiAccordionsItem';
import type { FsxFileSystemType } from '@/components/restore/restoreFsxWizard';
import { SnapshotIndicator } from '@/components/snapshots/SnapshotIndicator';
import { TextDetailsTuple } from '@/components/text/textDetailsTuple';
import { AccountAsVaultTag } from '@/components/vaults/vaultTag';
import { FsxVolumeInfo } from '@/components/volumes/volumeDetails';
import type { BackupVault } from '@/data/vaults/backupVault';
import { isDateValid } from '@/utils/dateTime';
import { dayjs, TIME_FORMAT } from '@/utils/dayjs';

import type { MutateSnapshots } from './eonSnapshotsTab';
import { SnapshotActionsGroup } from './snapshotActionsGroup';
import { SnapshotDetailsHeader } from './snapshotDetailsHeader';
import { SnapshotOptionMenu } from './snapshotOptionMenu';

// Temporary type until SDK is regenerated
export interface VolumeBackupInfo {
  volumeId?: string;
  name?: string;
  sizeBytes?: number;
  junctionPath?: string;
  volumeType?: string;
  recoveryPointArn?: string;
}

interface FsxSnapshotDetailsProps {
  snap: Snapshot;
  vault: BackupVault | undefined;
  isSelected: boolean;
  onSelect: () => void;
  onRestore: (selectedVolume?: VolumeBackupInfo) => void;
  resource: InventoryResource;
  snapshotSelection: boolean;
  mutateSnapshots: MutateSnapshots;
}

export const FsxSnapshotDetails = ({
  snap,
  vault,
  isSelected,
  onSelect,
  onRestore,
  resource,
  snapshotSelection,
  mutateSnapshots,
}: FsxSnapshotDetailsProps) => {
  // For native FSx snapshots (stored in AWS Backup), use custom header
  // For Eon-managed snapshots, use standard SnapshotDetailsHeader
  const isNativeSnapshot = snap.isNative ?? false;
  const fileSystemType = resource.resourceProperties?.awsFsx?.fileSystemType as
    | FsxFileSystemType
    | undefined;
  const isOntap = fileSystemType === 'ONTAP';

  // ONTAP supports volume-level restore (expandable row)
  // Other FSx types show simple row like S3 (non-expandable)
  if (isOntap) {
    return (
      <FsxOntapSnapshotDetails
        snap={snap}
        vault={vault}
        isSelected={isSelected}
        onSelect={onSelect}
        onRestore={onRestore}
        resource={resource}
        snapshotSelection={snapshotSelection}
        mutateSnapshots={mutateSnapshots}
        isNativeSnapshot={isNativeSnapshot}
      />
    );
  }

  // Non-ONTAP FSx types: simple row (non-expandable)
  return (
    <FsxSimpleSnapshotRow
      snap={snap}
      vault={vault}
      isSelected={isSelected}
      onSelect={onSelect}
      onRestore={() => onRestore()}
      resource={resource}
      snapshotSelection={snapshotSelection}
      mutateSnapshots={mutateSnapshots}
      isNativeSnapshot={isNativeSnapshot}
    />
  );
};

/**
 * Simple snapshot row for non-ONTAP FSx types (like S3 snapshots)
 */
const FsxSimpleSnapshotRow = ({
  snap,
  vault,
  isSelected,
  onSelect,
  onRestore,
  resource,
  snapshotSelection,
  mutateSnapshots,
  isNativeSnapshot,
}: {
  snap: Snapshot;
  vault: BackupVault | undefined;
  isSelected: boolean;
  onSelect: () => void;
  onRestore: () => void;
  resource: InventoryResource;
  snapshotSelection: boolean;
  mutateSnapshots: MutateSnapshots;
  isNativeSnapshot: boolean;
}) => {
  return (
    <Stack
      key={snap.id}
      gap='12px'
      data-testid='fsx-snapshot-row'
      borderBottom='1px solid var(--mui-palette-divider)'
      className='py-[24px] px-[40px] w-full'
      direction='row'
      justifyContent='space-between'
      alignItems='center'
    >
      {isNativeSnapshot ? (
        <FsxNativeSnapshotHeader
          snap={snap}
          resource={resource}
          isSelected={isSelected}
          onSelect={onSelect}
          snapshotSelection={snapshotSelection}
        />
      ) : (
        <SnapshotDetailsHeader
          snap={snap}
          vault={vault}
          isSelected={isSelected}
          onSelect={onSelect}
          snapshotSelection={snapshotSelection}
        />
      )}
      <SnapshotActionsGroup
        snap={snap}
        isVisible={!snapshotSelection || isSelected}
        onRestore={onRestore}
        resource={resource}
        mutateSnapshots={mutateSnapshots}
      />
    </Stack>
  );
};

/**
 * Expandable snapshot details for ONTAP FSx (supports volume selection)
 */
const FsxOntapSnapshotDetails = ({
  snap,
  vault,
  isSelected,
  onSelect,
  onRestore,
  resource,
  snapshotSelection,
  mutateSnapshots,
  isNativeSnapshot,
}: {
  snap: Snapshot;
  vault: BackupVault | undefined;
  isSelected: boolean;
  onSelect: () => void;
  onRestore: (selectedVolume?: VolumeBackupInfo) => void;
  resource: InventoryResource;
  snapshotSelection: boolean;
  mutateSnapshots: MutateSnapshots;
  isNativeSnapshot: boolean;
}) => {
  return (
    <MultiAccordionsItem
      testid='fsx-snapshot-row'
      sx={{ paddingRight: '40px' }}
      isMulti={snapshotSelection}
      isSelected={isSelected}
      onSelect={onSelect}
      summary={
        <Stack
          flex={1}
          direction='row'
          justifyContent='space-between'
          alignItems='center'
        >
          {isNativeSnapshot ? (
            <FsxNativeSnapshotHeader
              snap={snap}
              resource={resource}
              isSelected={isSelected}
              onSelect={onSelect}
              snapshotSelection={snapshotSelection}
            />
          ) : (
            <SnapshotDetailsHeader
              snap={snap}
              vault={vault}
              isSelected={isSelected}
              onSelect={onSelect}
              snapshotSelection={snapshotSelection}
            />
          )}
          <SnapshotOptionMenu
            snap={snap}
            mutateSnapshots={mutateSnapshots}
            resource={resource}
          />
        </Stack>
      }
      Details={
        <FsxOntapSnapshotDetailsBody
          snapshot={snap}
          resource={resource}
          onRestore={onRestore}
          mutateSnapshots={mutateSnapshots}
        />
      }
    />
  );
};

/**
 * Header for native FSx snapshots (stored in AWS Backup Vault)
 * Matches the EC2 SnapshotDetailsHeader layout but shows AWS Backup info
 */
const FsxNativeSnapshotHeader = ({
  snap,
  resource,
  isSelected,
  onSelect,
  snapshotSelection,
}: {
  snap: Snapshot;
  resource: InventoryResource;
  isSelected: boolean;
  onSelect: () => void;
  snapshotSelection: boolean;
}) => {
  const date = isDateValid(snap.pointInTime)
    ? snap.pointInTime
    : snap.createdTime;
  const fsxProps = snap.fsxSnapshotProperties;
  const region = fsxProps?.region || resource.region;

  return (
    <Stack
      className={classNames('flex-1', { 'cursor-pointer': snapshotSelection })}
      direction='row'
      gap='40px'
      alignItems='center'
      onClick={() => onSelect()}
    >
      <Stack direction='row' gap='60px' alignItems='center'>
        <Stack width='238px' direction='row' gap='36px' alignItems='center'>
          <Stack direction='row' gap='12px' alignItems='center'>
            {snapshotSelection && <Radio size='small' checked={isSelected} />}
            <SnapshotIndicator
              sx={{ margin: '6px' }}
              backgroundColor={null}
              isNative
            />
            <Typography width='100px' variant='subtitle1'>
              {`${dayjs.utc(date).format(TIME_FORMAT)} UTC`}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction='row' gap='12px' alignItems='center'>
          <Typography variant='subtitle1'>Stored in</Typography>
          <AccountAsVaultTag
            cloudProvider={resource.cloudProvider}
            region={region}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

interface FsxOntapSnapshotDetailsBodyProps {
  snapshot: Snapshot;
  resource: InventoryResource;
  onRestore: (selectedVolume?: VolumeBackupInfo) => void;
  mutateSnapshots: MutateSnapshots;
}

/**
 * Details body for ONTAP FSx snapshots - shows volume selection
 * Only specific volume restore is supported (not full file system)
 */
const FsxOntapSnapshotDetailsBody = ({
  snapshot,
  resource,
  onRestore,
  mutateSnapshots,
}: FsxOntapSnapshotDetailsBodyProps) => {
  const fsxProps = snapshot.fsxSnapshotProperties;
  // Use backedUpVolumes from snapshot (volumes actually backed up), not resource volumes
  const backedUpVolumes =
    (fsxProps?.backedUpVolumes as VolumeBackupInfo[]) || [];
  const [selectedVolume, setSelectedVolume] = useState<VolumeBackupInfo | null>(
    backedUpVolumes[0] || null
  );

  const handleRestore = () => {
    if (selectedVolume) {
      onRestore(selectedVolume);
    }
  };

  return (
    <Stack width='100%' direction='column' marginTop='12px'>
      <Stack direction='row' gap='40px' marginBottom='24px' marginLeft='36px'>
        <Stack overflow='hidden' flexGrow={1} direction='column' gap='12px'>
          <Typography marginBottom='8px' variant='subtitle1'>
            Choose a volume to restore
          </Typography>
          <Stack
            overflow='hidden'
            flexGrow={1}
            direction='column'
            gap='12px'
            divider={<Divider sx={{ borderStyle: 'dashed' }} />}
          >
            {backedUpVolumes.map((volume) => (
              <Stack
                direction='row'
                justifyContent='space-between'
                key={volume.volumeId}
                gap='12px'
              >
                <Stack
                  overflow='hidden'
                  className='cursor-pointer'
                  direction='row'
                  alignItems='center'
                  gap='12px'
                  onClick={() => setSelectedVolume(volume)}
                >
                  <Radio
                    data-testid='vol-selection-radio'
                    size='small'
                    checked={selectedVolume?.volumeId === volume.volumeId}
                  />
                  <TextDetailsTuple
                    primaryProp={
                      volume.junctionPath || volume.name || 'Unknown'
                    }
                    secondaryProp={volume.volumeId || ''}
                  />
                </Stack>
                <FsxVolumeInfo volume={volume} />
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Stack>

      <SnapshotActionsGroup
        mutateSnapshots={mutateSnapshots}
        onRestore={handleRestore}
        resource={resource}
        isVisible
      />
    </Stack>
  );
};
